import { apiClient } from './client'
import type { FormInstanceArtifactView, FormInstanceView, FormTemplateRef } from '../types/form'

/**
 * IB-168 — closes the disclosed Credit-tab gap: `GET /program-filings/{id}/form-instances`
 * (`FormInstanceController.listFinal`, backed by `FormInstanceService.listFinalByFiling`).
 * FINAL-only (draft rows are never included), capability-gated `form_instance:R` server-side (a
 * denied caller gets a thrown `ApiError` here, same as any other guarded read — this wrapper does
 * not swallow that). Empty array — never an error — when the filing has no finalized form yet.
 */
export async function listFinalFormInstancesByFiling(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<FormInstanceView[]> {
  const { data } = await apiClient.get<FormInstanceView[]>(
    `/program-filings/${programFilingId}/form-instances`,
    { signal },
  )
  return data
}

/**
 * `GET /form-templates/{id}` (canon, unguarded read) — resolves the human `formCode`/`name` behind
 * a `FormInstance.formTemplateId`. Mirrors `api/programs.ts`'s `getProgram` thin-wrapper shape.
 */
export async function getFormTemplateRef(formTemplateId: string, signal?: AbortSignal): Promise<FormTemplateRef> {
  const { data } = await apiClient.get<FormTemplateRef>(`/form-templates/${formTemplateId}`, { signal })
  return data
}

/**
 * Final-only artifact fetch (AC13/AC17, `@AuditedRead` server-side) — 404s if the instance is not
 * `final`. Mirrors `api/dossier.ts`'s `getDossierArtifact` contract.
 */
export async function getFormInstanceArtifact(formInstanceId: string): Promise<FormInstanceArtifactView> {
  const { data } = await apiClient.get<FormInstanceArtifactView>(`/form-instances/${formInstanceId}/artifact`)
  return data
}
